<template>
  <div
    ref="composer"
    class="ai-chat-composer"
    @dragenter.capture.prevent="onDragEnter"
    @dragover.capture.prevent="onDragOver"
    @dragleave.capture="onDragLeave"
    @drop.capture.prevent="onDrop"
  >
    <div
      :class="['_box', receivingDrop ? '-receiving-drop' : '']"
      @click="focus"
    >
      <ai-chat-attachment-tray
        v-if="attachments.length > 0"
        class="_tray"
        :attachments="attachments"
        removable
        @remove="dropAttachment"
      />
      <div class="_input">
        <m-editor
          ref="editor"
          :allowed-content="allowedContent"
          :custom-floating-buttons="customFloatingButtons"
          :placeholder="$t('aiChat.inputPlaceholder')"
          :enter-handlers="[onEnter]"
          split-key="Shift-Enter"
          disable-mobile-menu
          @input="onInput"
        />
      </div>
      <div class="_actions">
        <m-tooltip placement="top">
          <m-btn
            class="_attach"
            icon="plus"
            hide-border
            fab
            small
            @click="pickFiles"
          />
          <template #title>
            {{ $t('aiChat.attach.title') }}
          </template>
        </m-tooltip>
        <m-tooltip placement="top">
          <m-btn
            class="_send"
            icon="send"
            color="primary"
            fab
            small
            :disabled="!canSend"
            @click="submit"
          />
          <template #title>
            {{ $t('aiChat.send') }}
          </template>
        </m-tooltip>
      </div>
    </div>
    <input
      ref="fileInput"
      type="file"
      multiple
      :style="{ display: 'none' }"
      @change="filesChanged"
    >
  </div>
</template>

<script setup>
import AiChatAttachmentTray from '@/components/ai-chat/AiChatAttachmentTray.vue';
import MEditor from '@/components/editor/MEditor.vue';
import useSnackBar from '@/composables/snackbar';
import { AI_CHAT_ATTACHMENT_LIMIT, FILE_UPLOAD_LIMIT } from '@/lib/constants';
import { computed, ref } from 'vue';
import { docToMarkdown } from '@/lib/markdown';
import { editorNodeType } from 'shared/constants.json';
import { isImageName } from '@/lib/attachment';
import { logCatch } from '@/lib/logger/logger';
import { uploadFile } from '@/lib/image/image';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  pending: {
    type: Boolean,
    default: false,
  },
  send: {
    type: Function,
    required: true,
  },
});

const allowedContent = [editorNodeType.bulletList, editorNodeType.orderedList];

const { t } = useI18n();
const snackbar = useSnackBar();

const editor = ref(null);
const fileInput = ref(null);
const draft = ref(null);
const attachments = ref([]);
const composer = ref(null);
const receivingDrop = ref(false);

let nextKey = 0;

const markdown = computed(() => docToMarkdown(draft.value));
const uploading = computed(() => attachments.value.some((a) => a.uploading));
const canSend = computed(() => !props.pending
  && !uploading.value
  && (markdown.value !== '' || attachments.value.length > 0));

const focus = () => editor.value.focus();

const onInput = (doc) => { draft.value = doc; };

// the picker has to open inside the gesture that asked for it, so this runs no async work
// before click(); see the same constraint in ComponentList.js
const pickFiles = () => fileInput.value.click();

const customFloatingButtons = [{
  title: t('aiChat.attach.title'),
  subTitle: t('aiChat.attach.subTitle'),
  icon: 'paper-clip',
  onClick: pickFiles,
}];

const dropAttachment = (key) => {
  const index = attachments.value.findIndex((a) => a.key === key);
  if (index !== -1) {
    attachments.value.splice(index, 1);
  }
};

// the pushed object is only reachable reactively through the array proxy, so every later
// write has to go back through it; mutating the pushed literal leaves the tray stale
const patchAttachment = (key, changes) => {
  const tracked = attachments.value.find((a) => a.key === key);
  if (tracked !== undefined) {
    Object.assign(tracked, changes);
  }
};

const addAttachment = (file) => {
  const key = nextKey;
  nextKey += 1;

  // the thumbnail waits for the upload rather than previewing from an object url: the file
  // is on its way up anyway, and this is the very url the stored message will render
  attachments.value.push({
    key,
    name: file.name,
    isImage: isImageName(file.name),
    url: null,
    uploading: true,
    fileId: null,
  });

  return uploadFile(file)
    .then(({ fileId, getURL }) => { patchAttachment(key, { fileId, url: getURL, uploading: false }); })
    .catch(logCatch(() => {
      dropAttachment(key);
      snackbar.error();
    }));
};

const staged = (file) => attachments.value.some((a) => a.name === file.name);

const accept = (files) => {
  const accepted = files
    .filter((file) => {
      if (file.size > FILE_UPLOAD_LIMIT) {
        snackbar.error(t('fileUpload.fileTooBig'));
        return false;
      }

      // picking the same file twice would upload and send it twice, and the two chips
      // would be indistinguishable
      if (staged(file)) {
        snackbar.error(t('aiChat.attachmentAlreadyAdded', { name: file.name }));
        return false;
      }

      return true;
    });

  const room = Math.max(AI_CHAT_ATTACHMENT_LIMIT - attachments.value.length, 0);
  if (accepted.length > room) {
    snackbar.error(t('aiChat.attachmentLimitReached', { limit: AI_CHAT_ATTACHMENT_LIMIT }));
  }

  accepted.slice(0, room).forEach((file) => { addAttachment(file); });
};

const filesChanged = (event) => {
  accept([...event.target.files]);

  // only once the files have been read out: clearing it first can release them, and
  // re-picking the same file has to fire change again
  event.target.value = '';
};

const draggedFiles = (event) => [...(event.dataTransfer?.types ?? [])].includes('Files');

// prosemirror draws a drop cursor wherever a drag crosses it, offering to put the thing
// into the text. A file is not going there, so the editor never gets to see the drag.
const claimFileDrag = (event) => {
  if (!draggedFiles(event)) {
    return false;
  }

  event.stopPropagation();
  return true;
};

const onDragEnter = (event) => {
  if (claimFileDrag(event)) {
    receivingDrop.value = true;
  }
};

const onDragOver = (event) => {
  if (claimFileDrag(event)) {
    event.dataTransfer.dropEffect = 'copy';
  }
};

// moving onto a child of the composer also leaves the element under the pointer, and names
// the child as relatedTarget; only a leave that lands outside the composer really ends it
const onDragLeave = (event) => {
  if (!composer.value.contains(event.relatedTarget)) {
    receivingDrop.value = false;
  }
};

const onDrop = (event) => {
  receivingDrop.value = false;

  if (claimFileDrag(event)) {
    accept([...event.dataTransfer.files]);
  }
};

const submit = () => {
  if (!canSend.value) {
    return;
  }

  const sentDraft = draft.value;
  const sentAttachments = attachments.value;

  props.send(markdown.value, sentAttachments.map(
    ({ fileId, name, url }) => ({ fileId, name, getURL: url }),
  )).catch(logCatch(() => {
    attachments.value = [...sentAttachments, ...attachments.value];

    // whatever was typed while the send was in flight is newer than what failed to go out
    if (sentDraft !== null && markdown.value === '') {
      editor.value.setContent(sentDraft);
    }

    snackbar.error();
  }));

  attachments.value = [];

  editor.value.clearContent();
  draft.value = null;
};

// the emoji and slash menus are plugins, and plugin handlers run before every keymap, so an
// open menu takes Enter before it can reach this
const onEnter = () => {
  submit();
  return true;
};
</script>

<style
  scoped
  lang="scss"
  type="text/scss"
>
  @import "shared/assets/scss/_box-shadow.scss";

  .ai-chat-composer {
    position: relative;
    display: flex;
    flex-shrink: 0;
    max-height: 30%;
    padding: 0 1.2rem 1.2rem;

    // the conversation ends at the top edge of this box, so it would otherwise cut off mid
    // message; the fade sits above the box and stops where the box's right edge does, so it
    // never paints over the list's scroll bar
    &::before {
      content: '';
      position: absolute;
      right: 1.2rem;
      bottom: 100%;
      left: 0;
      height: $ai-chat-composer-fade-height;
      background: linear-gradient(to top, white, rgba(255, 255, 255, 0));
      pointer-events: none;
    }

    ._box {
      transition: background-color .1s ease-in-out, box-shadow .1s ease-in-out;

      // positioned so it paints after the fade above it, which would otherwise cover the top
      // of the 1px ring that draws the box's edge
      position: relative;
      display: flex;
      flex-direction: column;
      width: 100%;
      min-height: 0;
      padding: .8rem;
      background-color: white;
      border-radius: 2rem;
      cursor: text;

      @include box_shadow(1);

      &.-receiving-drop {
        background-color: $highlighted-color-light;
        box-shadow: 0 0 0 2px $primary-color;
      }
    }

    ._tray {
      padding: .4rem .2rem .2rem;
    }

    ._input {
      flex: 1;
      min-height: 0;
      padding: .4rem .2rem;
      overflow-y: auto;
    }

    ._actions {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-shrink: 0;
      gap: .4rem;
      padding: .2rem;
    }

    ._send {
      border-radius: 50%;
    }
  }
</style>
